/**
 * "Who can join?" at the end of a shared session's privacy line (TMMAPP-0164 item 13; TMMAPI-0195
 * U4, MM-D-149 amended). A small popover (the v2 menu surface) says the rule in one line, lists each
 * member with whether they can ask the assistant, and gives the owner **Manage sharing**, which opens
 * the virtual account's sharing dialog (the one Virtual Accounts opens, where each share's **Allow
 * the assistant** is).
 *
 * Every ACTIVE share of the virtual account, guests included, is a member; Allow the assistant only
 * decides who can ask, on the owner's AI credit. A reason ("Not allowed", "This month's limit is
 * used", "AI is off") shows where it's served: on the caller's own row and, to the owner, on every
 * row; anyone else's reads "Can't ask the assistant". The copy never says joining changes what the
 * AI reads (MM-D-94).
 */
import { useId, useState, type MouseEvent } from 'react';
import { Box, Link, Popover, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AssistantAskReason, AssistantSession } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { SharingDialog } from '@/features/virtualAccount/SharingDialog';
import { personName } from '@/i18n/personName';
import { useNotify } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';

const REASON_KEYS = {
  NOT_ALLOWED: 'assistant.whoCanJoin.reason.NOT_ALLOWED',
  LIMIT_REACHED: 'assistant.whoCanJoin.reason.LIMIT_REACHED',
  AI_OFF: 'assistant.whoCanJoin.reason.AI_OFF',
} as const satisfies Record<AssistantAskReason, string>;

export function WhoCanJoin({
  session,
  virtualAccountName,
  ownerName,
  onSharingChanged,
}: {
  session: AssistantSession;
  virtualAccountName: string | null;
  /** The owner's name when the caller isn't the owner; null when unknown. */
  ownerName: string | null;
  /** A share changed in the sharing dialog: the session's members are read again. */
  onSharingChanged: () => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const notify = useNotify();
  const titleId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [detail, setDetail] = useState<VirtualAccountDetail | null>(null);
  const [opening, setOpening] = useState(false);
  const owner = session.members.some((member) => member.mine && member.owner);
  const virtualAccount = virtualAccountName ?? t('assistant.whoCanJoin.thisVirtualAccount');
  const rule = owner
    ? t('assistant.whoCanJoin.ruleOwner', { virtualAccount })
    : ownerName !== null && ownerName.trim().length > 0
      ? t('assistant.whoCanJoin.ruleMember', { virtualAccount, owner: ownerName })
      : t('assistant.whoCanJoin.ruleMemberUnnamed', { virtualAccount });

  async function manageSharing() {
    const id = session.virtualAccountId;
    if (id === null || opening) return;
    setAnchor(null);
    setOpening(true);
    try {
      setDetail(await virtualAccountsApi.get(id));
    } catch (error) {
      notify(errorCopy(t, error, 'assistant.whoCanJoin.sharingUnavailable'), { severity: 'error' });
    } finally {
      setOpening(false);
    }
  }

  return (
    <>
      <Link
        component="button"
        type="button"
        data-who-can-join=""
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        sx={{ fontSize: 13, lineHeight: '18px', flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {t('assistant.whoCanJoin.link')}
      </Link>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': titleId,
            sx: { p: 2, maxWidth: 360, width: 'calc(100vw - 32px)' },
          } as object,
        }}
      >
        <Stack spacing={1.25} data-who-can-join-panel="">
          <Typography id={titleId} component="h3" sx={{ fontSize: 15, fontWeight: 600, m: 0 }}>
            {t('assistant.whoCanJoin.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text2 }}>{rule}</Typography>
          <Box
            component="ul"
            aria-label={t('assistant.whoCanJoin.membersLabel')}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {session.members.map((member, index) => {
              const name = member.mine ? t('assistant.you') : personName(member.name, t);
              const status = member.canAsk
                ? t('assistant.whoCanJoin.canAsk')
                : // A reason is the member's own or, to the owner, everyone's (TMMAPI-0195 U4).
                  (member.mine || owner) && member.canAskReason !== null
                  ? t(REASON_KEYS[member.canAskReason])
                  : t('assistant.whoCanJoin.cantAsk');
              return (
                <Box
                  component="li"
                  key={`${index}-${member.name ?? ''}`}
                  data-who-can-join-member={member.canAsk ? 'can' : 'cant'}
                  sx={{
                    py: 0.75,
                    borderTop: `1px solid ${tokens.line}`,
                    '&:first-of-type': { borderTop: 0 },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {member.owner ? t('assistant.whoCanJoin.ownerName', { name }) : name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: tokens.text2 }}>{status}</Typography>
                </Box>
              );
            })}
          </Box>
          {owner && session.virtualAccountId !== null && (
            <Link
              component="button"
              type="button"
              onClick={() => void manageSharing()}
              data-manage-sharing=""
              sx={{ alignSelf: 'flex-start', fontSize: 14 }}
            >
              {t('assistant.whoCanJoin.manageSharing')}
            </Link>
          )}
        </Stack>
      </Popover>
      {detail !== null && (
        <SharingDialog
          detail={detail}
          onClose={() => setDetail(null)}
          onChanged={async () => {
            const next = await virtualAccountsApi.get(detail.id);
            setDetail(next);
            onSharingChanged();
          }}
          onNotify={(message) => notify(message)}
          onLeft={() => setDetail(null)}
        />
      )}
    </>
  );
}
